import Cookie from 'js-cookie'
import ReactGA from 'react-ga4'

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

const SCORING_COOKIES_CONSENT = 'scoring-cookies-consent'
const GOOGLE_ANALYTICS = 'google-analytics'

/** GA's own cookies (`_ga`, `_ga_<stream>`), on the host and on the registrable domain gtag.js uses. */
const removeGoogleAnalyticsCookies = () => {
    const domain = '.' + window.location.hostname.split('.').slice(-2).join('.')
    Object.keys(Cookie.get())
        .filter((name) => name === '_ga' || name.startsWith('_ga_'))
        .forEach((name) => {
            Cookie.remove(name)
            Cookie.remove(name, { path: '/', domain })
        })
}

/**
 * `disable` = true: nothing of Google's runs. The opt-out flag is set FIRST so an already-loaded
 * gtag.js stops at once, react-ga4 is reset, and GA's cookies are dropped (consent withdrawn or
 * never given). No gtag call at all on this path: a `config` command is what loads the tag and
 * writes `_ga*`, so issuing one "to disable" is self-defeating.
 * `disable` = false: load gtag.js and allow signals (consent given).
 */
const googleAnalyticsSwitching = (disable) => {
    window['ga-disable-' + GA_MEASUREMENT_ID] = disable

    if (disable) {
        ReactGA.reset()
        removeGoogleAnalyticsCookies()
    } else {
        // Audience measurement only — that is what the visitor consented to. Google Signals and
        // ad personalisation are advertising features (cross-device, remarketing) and stay off.
        ReactGA.initialize(GA_MEASUREMENT_ID, {
            gtagOptions: { allow_google_signals: false, allow_ad_personalization_signals: false },
        })
    }

    console.log(`GOOGLE ANALYTICS - ${disable ? 'DISABLED' : 'ENABLED'}`)
}

const readConsent = () => {
    const raw = Cookie.get(SCORING_COOKIES_CONSENT)
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'object' && parsed ? parsed : null
    } catch {
        Cookie.remove(SCORING_COOKIES_CONSENT)
        return null
    }
}

/** Whether a choice is stored at all (pure, no analytics switching): the welcome card waits for it. */
export const hasConsent = () => !!readConsent()

/** The stored analytics choice (false when nothing is stored): pre-fills the preferences switch. */
export const readAnalyticsConsent = () => !!readConsent()?.performance?.[GOOGLE_ANALYTICS]?.consent

/**
 * Apply the stored choice at start-up. No choice yet (or an unreadable one) = analytics OFF:
 * a non-essential tracker needs prior consent (ePrivacy / art. 82 LIL, CNIL 2020 guidelines),
 * so GA only starts once the visitor has said yes.
 */
export const consetPreferenceGiven = () => {
    const cookiesConsent = readConsent()

    if (!cookiesConsent) {
        googleAnalyticsSwitching(true)
        return false
    }

    googleAnalyticsSwitching(!cookiesConsent?.performance?.[GOOGLE_ANALYTICS]?.consent)
    return true
}

/**
 * Store the choice and switch Google Analytics accordingly. Audience measurement is the only
 * optional category (the support chat is essential — William, Sept 30 2026), so "Accept all" is
 * `saveConsent(true)`, "Continue without accepting" is `saveConsent(false)` and "Save my choices"
 * passes the switch.
 */
/** Bump when the banner's wording or categories change: an older recorded consent no longer covers them. */
const CONSENT_VERSION = 2

export const saveConsent = (analyticsAccepted) => {
    const cookiesConsent = {
        performance: {
            [GOOGLE_ANALYTICS]: { consent: !!analyticsAccepted },
        },
        // Proof of consent (CNIL): when it was given and against which banner.
        date: new Date().toISOString(),
        version: CONSENT_VERSION,
    }

    Cookie.remove(SCORING_COOKIES_CONSENT)
    Cookie.set(SCORING_COOKIES_CONSENT, JSON.stringify(cookiesConsent), {
        sameSite: 'strict',
        // CNIL: a consent choice is kept 6 months at most, then the question is asked again.
        expires: 182,
    })

    googleAnalyticsSwitching(!analyticsAccepted)
}
